'use client';

import { AlertCircle, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { WIND_DOWN, WIND_DOWN_DISPLAY } from '@/features/common/config/windDown';
import { useAuth } from '@/shared/auth';
import { CommonModal } from './CommonModal';

const STORAGE_KEY = 'windDownBannerDismissed';

/** Show banner starting from Feb 20 PST (= Feb 21 UTC 08:00) */
const NOTICE_START = new Date('2026-02-21T08:00:00.000Z');

function formatShort(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
}

function formatLong(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
}

export function WindDownBanner() {
  const [visible, setVisible] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const user = useAuth(s => s.user);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (new Date() < NOTICE_START) {
      return;
    }

    try {
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        return;
      }
    } catch {
      // sessionStorage unavailable
    }

    setVisible(true);
  }, [user]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDetailOpen(true)}
        className="w-full border-amber-200 border-b bg-amber-50 text-left transition-colors hover:bg-amber-100"
      >
        <div className="mx-auto flex max-w-screen-md items-center gap-3 px-4 py-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-600" />
          <span className="flex-1 font-mona-sans text-amber-900 text-body-2-normal-regular">
            <span className="font-semibold">
              Submissions close {formatShort(WIND_DOWN_DISPLAY.SUBMISSION_DEADLINE)}.
            </span>{' '}
            Tap for details.
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-amber-400" />
          <span
            role="button"
            tabIndex={0}
            onClick={handleDismiss}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleDismiss(e as unknown as React.MouseEvent);
              }
            }}
            className="flex-shrink-0 rounded p-0.5 text-amber-600 hover:bg-amber-200 hover:text-amber-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </span>
        </div>
      </button>

      <CommonModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Important Update"
        image={
          <div className="flex h-[80px] w-[80px] items-center justify-center rounded-full bg-amber-100">
            <AlertCircle className="h-10 w-10 text-amber-600" />
          </div>
        }
        description={
          <div className="flex flex-col gap-3 font-mona-sans text-body-1-normal-regular text-semantic-label-normal">
            <p>
              Submissions will close on{' '}
              <span className="font-semibold">
                {formatLong(WIND_DOWN_DISPLAY.SUBMISSION_DEADLINE)}
              </span>
              . No new videos can be submitted after this date.
            </p>
            <p>
              You can still withdraw your P balance until{' '}
              <span className="font-semibold">
                {formatLong(WIND_DOWN_DISPLAY.EXCHANGE_DEADLINE)}
              </span>
              . The minimum has been lowered to $1 (20 P).
            </p>
            <p>Any remaining balance after the deadline will expire.</p>
          </div>
        }
        primaryActionText="Go to Exchange"
        onPrimaryAction={() => {
          setDetailOpen(false);
          window.location.href = '/mypage/exchange';
        }}
        secondaryActionText="Got it"
        onSecondaryAction={() => setDetailOpen(false)}
      />
    </>
  );
}
